import { AbsoluteFill, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from 'remotion';
import { useCallback, useMemo, useRef } from 'react';

import { VIDEO_ASPECT_RATIO } from '@goldcast/player-config/src/constants/player';
import { VideoCropPosition } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import {
  SelectionElementType,
  CompositionConfig,
  ElementPositions
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { constrainElementPosition } from '../ElementComponent/ImageElementComponent/utils';
import { useMediaBounds, useElementPositions } from '../ElementComponent/hooks/useMediaBounds';
import SpeakerLabelContainer from '../SpeakerLabel/SpeakerLabelContainer';
import { getAnimationStylesForFrame } from '../../MainPlayer/utils/animation/getAnimationStylesForFrame';
import { SelectionOutline } from '../Outlines/SelectionOutline';
import { useTransitionStyles } from '../ElementComponent/hooks/useTransitionStyles';

import { VideoRenderer } from './components/VideoRenderer';
import { useRemotionUsage } from '../../MainPlayer/RemotionUsageContext/RemotionUsageContext';

const VideoElementComponent: React.FC<{
  element: VideoElement;
  disableMediaFragmentHints?: boolean;
  ignoreEndAt?: boolean;
  canEditSpeakerLabel: boolean;
  canEditVideos?: boolean;
  canManuallyReposition?: boolean;
  onOutlineRelease?: CompositionConfig['onOutlineRelease'];
  layoutChangeFrame?: number;
  cropInformation?: VideoCropPosition;
  compositeSegmentId?: string;
  animationStylesUserVideo?: React.CSSProperties;
  offset?: number;
  shouldHideSpeakerLabels?: boolean;
}> = ({
  element,
  disableMediaFragmentHints = false,
  ignoreEndAt = false,
  canEditSpeakerLabel = false,
  canEditVideos = false,
  onOutlineRelease,
  canManuallyReposition,
  layoutChangeFrame,
  cropInformation,
  animationStylesUserVideo,
  compositeSegmentId,
  offset = 0,
  shouldHideSpeakerLabels = false
}) => {
  const { details, style, containerStyle, effects } = element;

  const transitionStyles = useTransitionStyles({ element });

  const videoElementRef = useRef<HTMLDivElement>(null);

  const frame = useCurrentFrame();
  const currentFrame = frame + offset;
  const { fps } = useVideoConfig();
  const isPreview = !getRemotionEnvironment().isRendering;
  const { style: animationStyle } = getAnimationStylesForFrame(currentFrame, effects || [], fps);

  const { useNewVideoTag } = useRemotionUsage();

  const getVideoBounds = useMediaBounds(videoElementRef, useNewVideoTag ? 'canvas' : 'video');

  const isVideoStyleContain = useMemo(() => style.objectFit === 'contain', [style.objectFit]);

  const { positions, updatePositions, setPositions } = useElementPositions(
    containerStyle,
    isVideoStyleContain,
    getVideoBounds
  );

  const transformedContainerStyle = useMemo(() => {
    return {
      ...containerStyle,
      ...animationStyle,
      ...(canEditVideos ? positions : {})
    };
  }, [containerStyle, animationStyle, positions, canEditVideos]);

  // Handle video load for contain mode
  const handleVideoLoad = useCallback(() => {
    if (!canEditVideos) {
      return;
    }

    if (!isVideoStyleContain) {
      return;
    }

    const videoBounds = getVideoBounds();
    if (videoBounds) {
      setPositions({
        top: `${videoBounds.top}%`,
        left: `${videoBounds.left}%`,
        width: `${videoBounds.width}%`,
        height: `${videoBounds.height}%`
      });
    }
  }, [isVideoStyleContain, getVideoBounds, setPositions, canEditVideos]);

  const onSelectionOutlineRelease = useCallback(
    (positions: ElementPositions) => {
      const parentElement = videoElementRef.current?.parentElement;
      const parentDimensions = parentElement ? parentElement.getBoundingClientRect() : null;

      const constrainedPositions = constrainElementPosition(positions, parentDimensions);

      setPositions(constrainedPositions);
      onOutlineRelease?.(constrainedPositions, element);
    },
    [onOutlineRelease, element, setPositions]
  );

  return (
    <>
      <AbsoluteFill
        data-testid="video-element-preview"
        ref={videoElementRef}
        style={{ ...transformedContainerStyle, containerType: 'inline-size' }}
      >
        <div
          style={
            animationStylesUserVideo && Object.keys(animationStylesUserVideo).length > 0
              ? { width: '100%', height: '100%', ...animationStylesUserVideo, ...transitionStyles }
              : { width: '100%', height: '100%', ...transitionStyles }
          }
        >
          <VideoRenderer
            element={element}
            ignoreEndAt={ignoreEndAt}
            disableMediaFragmentHints={disableMediaFragmentHints}
            handleVideoLoad={handleVideoLoad}
          />
        </div>

        {element.speakerDetails && element.speakerLabel && (
          <SpeakerLabelContainer
            speakerLabel={element.speakerLabel}
            canEditSpeakerLabel={canEditSpeakerLabel}
            speakerId={element.speakerDetails.id}
            layoutChangeFrame={layoutChangeFrame}
            isAudiogram={false}
            isSmallVideo={shouldHideSpeakerLabels}
            offset={offset}
          />
        )}
      </AbsoluteFill>
      {canEditVideos && (
        <SelectionOutline
          positions={positions}
          updatePositions={updatePositions}
          onOutlineRelease={onSelectionOutlineRelease}
          allowResize={true}
          elementType={SelectionElementType.VIDEO}
          data={{ elementId: element.id, type: SelectionElementType.VIDEO }}
          maintainAspectRatio={isVideoStyleContain}
        />
      )}
      {canManuallyReposition && compositeSegmentId && (
        <SelectionOutline
          positions={transformedContainerStyle}
          updatePositions={() => {}}
          onOutlineRelease={onSelectionOutlineRelease}
          allowResize={false}
          disableDrag={true}
          elementType={SelectionElementType.VIDEO_IN_COMPOSITE}
          data={{
            elementId: element.id,
            type: SelectionElementType.VIDEO_IN_COMPOSITE,
            videoElementRef,
            cropInformation,
            videoAspectRatio: element.aspectRatio ?? VIDEO_ASPECT_RATIO,
            compositeSegmentId: compositeSegmentId,
            videoUrl: isPreview && details.preview ? details.preview : details.src
          }}
        />
      )}
    </>
  );
};

export default VideoElementComponent;
